import { ColorContext } from 'App';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import Popup from 'reactjs-popup';
import 'styles/global.css';
import styles from './FusionDropDownMultiSelect.module.css';

/**
 * The standard Fusion drop down input field.
 * @param {{style: Object, value: any[], onChange: (value: any[]), disabled?: boolean, placeholder?: string, children: Component[]}}
 * @example
 * const [value, setValue] = useState([]);  //possible values: [1, 2, 3]
 *
 * return <FusionDropDownMultiSelect
 *  value={value}
 *  onChange={(newValue) => setValue(newValue)}
 * >
 *  <option value={1}>Value 1</option>
 *  <option value={2}>Value 2</option>
 *  <option value={3}>Value 3</option>
 * </FusionDropDownMultiSelect>
 */
export default function FusionDropDownMultiSelect({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select Options...',
  label = '',
  required = false,
  children,
  arrow = false,
}) {
  const triggerRef = useRef(null);
  const [triggerWidth, setTriggerWidth] = useState(null);

  const updateTriggerWidth = () => {
    if (typeof window === 'undefined') return;
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  };

  useEffect(() => {
    updateTriggerWidth();
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('resize', updateTriggerWidth);
    return () => window.removeEventListener('resize', updateTriggerWidth);
  }, []);

  const DownArrowIcon = (
    <svg
      width="2em"
      height="1em"
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="position-absolute end-0 top-50 translate-middle-y text-secondary"
      style={{ pointerEvents: 'none', right: 8 }}
    >
      <path
        fill="white"
        d="M4.646 6.646a.5.5 0 0 1 .708 0L8 9.293l2.646-2.647a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 0-.708z"
      />
    </svg>
  );

  const OptionRow = ({ option, key }) => {
    return (
      <div
        key={key}
        className="d-flex w-100 gap-3 p-1"
        onClick={() => {
          const newValue = [...value];

          if (!newValue.includes(option.props.value)) newValue.push(option.props.value);
          else newValue.splice(newValue.indexOf(option.props.value), 1);

          if (value !== newValue) onChange(newValue);
        }}
      >
        <Form.Check
          type="checkbox"
          checked={value.find((v) => v === option.props.value)}
          onChange={() => {}} //This is to remove the warnings
        />
        {option}
      </div>
    );
  };

  return (
    <>
      <Popup
        disabled={disabled}
        trigger={
          <Form.Group>
            {label && (
              <Form.Label className="bg-transparent fs-6" htmlFor="roles">
                {label}
                {required && <span className="text-danger ms-1">*</span>}
              </Form.Label>
            )}
            <Button
              ref={triggerRef}
              className="w-100 bg-primary rounded-2 border border-1 border-secondary position-relative pe-5"
              id="roles"
              variant="secondary"
            >
              {children &&
                value.length > 0 &&
                children.map((child, childIndex) => {
                  if (Array.isArray(child))
                    return child
                      .filter(
                        (filter) => filter.type === 'option' && value.includes(filter.props.value),
                      )
                      .map((subChild, subIndex) => (
                        <div key={`${childIndex}-${subIndex}`} className="d-flex">
                          {subChild},{' '}
                        </div>
                      ));
                  else if (child.type === 'option' && value.includes(child.props.value))
                    return (
                      <div key={childIndex} className="d-flex">
                        {child},{' '}
                      </div>
                    );

                  return <React.Fragment key={childIndex}></React.Fragment>;
                })}
              {value.length === 0 && !disabled && (
                <div className="text-secondary">{placeholder}</div>
              )}
              {/* Down arrow Icon */}
              {arrow && DownArrowIcon}
            </Button>
          </Form.Group>
        }
        position={'bottom left'}
        offsetX={0}
        offsetY={0}
        arrow={arrow}
        onOpen={updateTriggerWidth}
        contentStyle={{
          width: triggerWidth ? `${triggerWidth}px` : 'auto',
          minWidth: triggerWidth ? `${triggerWidth}px` : undefined,
          padding: 0,
          margin: 0,
          border: 'none',
          borderRadius: 0,
          boxShadow: 'none',
        }}
        overlayStyle={{ padding: 0 }}
      >
        <div
          className="fusion-input cursor-pointer user-select-none text-white"
          style={{ width: '100%', marginTop: 0 }}
        >
          {children &&
            children.map((child, index) => {
              if (Array.isArray(child))
                return child.map((subChild, subIndex) =>
                  subChild.type === 'option' ? (
                    <OptionRow key={index * 1000 + subIndex} option={subChild} />
                  ) : (
                    subChild
                  ),
                );
              else if (child.type === 'option') return <OptionRow key={index} option={child} />;
              else return child;
            })}
        </div>
      </Popup>
    </>
  );
}
